import type { QueryClient } from '@tanstack/react-query';

import { getAuthState, useAuthStore } from '@/shared/auth/store';
import { REALTIME_RECONNECTED, type RealtimeManager } from '@/shared/realtime';

import { cacheMessage, cacheMessageUpdate, cacheRead, forgetChat, messengerKeys } from './api';
import { callManager } from './calls/callManager';
import { useCallStore } from './calls/callStore';
import { countIncoming } from './lib/cache';
import { useMessengerStore } from './store';
import type {
  Call,
  CallEnded,
  ChatDetails,
  ChatEventPayload,
  ChatReadPayload,
  ChatRemovedPayload,
  Message,
  TypingPayload,
  UnreadSummary,
} from './types';

/** Realtime event types of the messenger (docs/modules/messenger.md). */
export const MessengerEvents = {
  messageCreated: 'messenger.message-created',
  messageUpdated: 'messenger.message-updated',
  messageDeleted: 'messenger.message-deleted',
  chatRead: 'messenger.chat-read',
  typing: 'messenger.typing',
  chatCreated: 'messenger.chat-created',
  chatUpdated: 'messenger.chat-updated',
  chatRemoved: 'messenger.chat-removed',
  foldersUpdated: 'messenger.folders-updated',
  callRinging: 'messenger.call-ringing',
  callUpdated: 'messenger.call-updated',
  callEnded: 'messenger.call-ended',
} as const;

/** How long the folder badges wait for more messages before they ask the server. */
export const FOLDERS_REFRESH_MS = 800;

const me = () => getAuthState().user?.id ?? '';

/** Coalesces bursts of invalidations (e.g. many messages arriving at once). */
function debounced(action: () => void, ms: number): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(action, ms);
  };
}

function setActiveCall(queryClient: QueryClient, chatId: string, call: Call | null): void {
  queryClient.setQueryData<ChatDetails>(messengerKeys.chat(chatId), (chat) =>
    chat ? { ...chat, activeCall: call } : chat,
  );
}

/**
 * Keeps the messenger caches live (module `setup`): messages, previews and counters, read receipts, typing, chat
 * changes and calls. UI reactions (toasts, the incoming call prompt) live in components.
 */
export function setupMessengerRealtime(queryClient: QueryClient, realtime: RealtimeManager): void {
  // Nothing of a user may outlive their session in this tab: drafts, typing, the call and its signaling connection.
  const userOf = (state: ReturnType<typeof getAuthState>) =>
    state.status === 'authenticated' ? (state.user?.id ?? null) : null;
  let signedIn = userOf(getAuthState());
  useAuthStore.subscribe((state) => {
    const user = userOf(state);
    if (user === signedIn) return;
    const previous = signedIn;
    signedIn = user;
    if (previous === null) return;
    useMessengerStore.getState().reset();
    void callManager.disconnect();
  });

  const refreshUnread = debounced(
    () => void queryClient.invalidateQueries({ queryKey: messengerKeys.unread() }),
    300,
  );
  // The badges of the folder tabs: messages and reads change them in bursts.
  const refreshFolders = debounced(
    () => void queryClient.invalidateQueries({ queryKey: messengerKeys.folders() }),
    FOLDERS_REFRESH_MS,
  );

  realtime.on<Message>(MessengerEvents.messageCreated, (message) => {
    const ui = useMessengerStore.getState();
    const readNow = ui.openChatId === message.chatId && ui.openChatAtEnd;
    cacheMessage(queryClient, message, { readNow });
    refreshFolders();
    if (message.author) ui.clearTyping(message.chatId, message.author.id);
    // The navigation counter counts the message itself: every online member gets this event, and a refetch from each
    // of them would recount every chat they are in. Only those watching the chat ask the server (read right away
    // or not, depending on focus).
    if (message.author?.id === me()) return;
    if (readNow) {
      refreshUnread();
      return;
    }
    const summary = queryClient.getQueryData<UnreadSummary>(messengerKeys.unread());
    if (!summary) return;
    const next = countIncoming(summary, message.chatId);
    if (next) queryClient.setQueryData(messengerKeys.unread(), next);
    else refreshUnread();
  });

  const onUpdate = (message: Message) => cacheMessageUpdate(queryClient, message);
  realtime.on<Message>(MessengerEvents.messageUpdated, onUpdate);
  realtime.on<Message>(MessengerEvents.messageDeleted, (message) => {
    onUpdate(message);
    refreshUnread();
  });

  realtime.on<ChatReadPayload>(MessengerEvents.chatRead, ({ chatId, userId, lastReadSeq }) => {
    if (userId === me()) {
      cacheRead(queryClient, chatId, lastReadSeq);
      refreshFolders();
      return;
    }
    queryClient.setQueryData<ChatDetails>(messengerKeys.chat(chatId), (chat) =>
      chat
        ? {
            ...chat,
            members: chat.members.map((m) =>
              m.user.id === userId && m.lastReadSeq < lastReadSeq ? { ...m, lastReadSeq } : m,
            ),
          }
        : chat,
    );
  });

  realtime.on<TypingPayload>(MessengerEvents.typing, ({ chatId, userId, fullName }) => {
    if (userId !== me()) useMessengerStore.getState().setTyping(chatId, userId, fullName);
  });

  realtime.on<ChatEventPayload>(MessengerEvents.chatCreated, ({ chatId }) => {
    // Added (back) to a chat.
    useMessengerStore.getState().setGone(chatId, false);
    void queryClient.invalidateQueries({ queryKey: messengerKeys.chats() });
    refreshUnread();
  });

  // Also sent to the person's own tabs when they pin, archive, put into a folder, mark as unread or clear a chat.
  realtime.on<ChatEventPayload>(MessengerEvents.chatUpdated, ({ chatId }) => {
    void queryClient.invalidateQueries({ queryKey: messengerKeys.chats() });
    void queryClient.invalidateQueries({ queryKey: messengerKeys.chat(chatId) });
    refreshUnread();
    refreshFolders();
  });

  // Folders were created, renamed, deleted or ordered in another tab.
  realtime.on(MessengerEvents.foldersUpdated, () => {
    void queryClient.invalidateQueries({ queryKey: messengerKeys.folders() });
  });

  // `reason: 'deleted'` (the owner deleted the chat) is told to the person by the page that has the chat open.
  realtime.on<ChatRemovedPayload>(MessengerEvents.chatRemoved, ({ chatId }) => forgetChat(queryClient, chatId));

  realtime.on<Call>(MessengerEvents.callRinging, (call) => {
    setActiveCall(queryClient, call.chatId, call);
    const current = useCallStore.getState().call;
    if (call.initiator?.id !== me() && current?.id !== call.id) useCallStore.getState().addIncoming(call);
  });

  realtime.on<Call>(MessengerEvents.callUpdated, (call) => {
    setActiveCall(queryClient, call.chatId, call);
    useCallStore.getState().updateIncoming(call, me());
    callManager.syncCall(call);
  });

  realtime.on<CallEnded>(MessengerEvents.callEnded, (ended) => {
    setActiveCall(queryClient, ended.chatId, null);
    useCallStore.getState().removeIncoming(ended.id);
    callManager.end(ended);
  });

  // Events may have been missed while disconnected.
  realtime.on(
    REALTIME_RECONNECTED,
    () => void queryClient.invalidateQueries({ queryKey: messengerKeys.all }),
  );
}
